import { JpegFrameInfo, stripCarrySegments } from './jpeg-segment';
import { CanvasMatrix } from './orientation';

/**
 * canvas による回転。jpegtran のロスレス変換が使えない画像のフォールバック。
 *
 * 画素は一度再エンコードされるが、縦横の画素数は厳密に保存される
 * (canvas のサイズを明示し等倍で描画するため)。
 */

const loadImage = (blob: Blob): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(blob);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(url);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('回転対象の画像をデコードできませんでした'));
  };
  image.src = url;
});

const toBytes = (blob: Blob): Promise<Uint8Array> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
  reader.onerror = () => reject(new Error('回転結果を読み出せませんでした'));
  reader.readAsArrayBuffer(blob);
});

/**
 * @param sourceBytes 元の JPEG バイナリ
 * @param frame SOF から読んだ実寸 (格納されている画素の寸法)
 * @param matrix 正しい向きで描くための変換行列
 * @param swapsAxis 縦横が入れ替わるか
 * @param quality toBlob に渡す品質
 */
export const rotateWithCanvas = async (
  sourceBytes: Uint8Array,
  frame: JpegFrameInfo,
  matrix: CanvasMatrix,
  swapsAxis: boolean,
  quality: number,
): Promise<Uint8Array> => {
  /*
   * ブラウザは Exif を見て自動回転してしまうため、デコード前に Exif ごと剥がす。
   * createImageBitmap の imageOrientation に頼るとブラウザ差を踏むので、
   * 「回転情報が存在しない画像を読ませる」ことで確実に素の画素を得る。
   * ICC も一緒に剥がれるが、これにより色変換を挟まず元の画素値がそのまま得られる。
   * Exif / ICC は呼び出し側が変換後に元のセグメントを移植して復元する。
   */
  const naked = stripCarrySegments(sourceBytes);
  const image = await loadImage(new Blob([naked], { type: 'image/jpeg' }));

  if (image.naturalWidth !== frame.width || image.naturalHeight !== frame.height) {
    throw new Error(
      `デコード結果が SOF と一致しません (SOF: ${frame.width}x${frame.height}, `
      + `デコード: ${image.naturalWidth}x${image.naturalHeight})`,
    );
  }

  const canvas = document.createElement('canvas');
  canvas.width = swapsAxis ? frame.height : frame.width;
  canvas.height = swapsAxis ? frame.width : frame.height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('2d コンテキストを取得できませんでした');
  }
  context.setTransform(...matrix);
  context.drawImage(image, 0, 0);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });
  if (!blob) {
    throw new Error('回転結果をエンコードできませんでした');
  }
  return toBytes(blob);
};
